import React, { useEffect } from 'react';

import { Logger } from '../Logging';

import { StatsigProvider, useClientAsyncInit } from './StatsigLib';
import { useRoverStatsigUser } from './useRoverStatsigUser';
import { createUserPayload, getApiKey, getStatsigClientOptions, userNeedsUpdate } from './utils';

export type Props = {
  children: React.ReactNode;
};

const RoverStatsigProvider = ({ children }: Props): JSX.Element => {
  const { roverStatsigUser } = useRoverStatsigUser();

  // Initialize Statsig client asynchronously with the API key and any initial user data, from
  // https://docs.statsig.com/client/javascript-sdk/init-strategies/#3-asynchronous-initialization---not-awaited
  const { client } = useClientAsyncInit(getApiKey(), roverStatsigUser, getStatsigClientOptions());

  // watch the roverStatsigUser, call updateUserAsync if it changes
  // through the magic of React context all usages of Statsig hooks will update after this
  // MAYBE FUTURE TODO: this fires a bit more than it really needs to but it doesn't _seem_ to hurt anything? This happens because:
  //    - sometimes roverStatsigUser is an empty object, which we now handle below
  //    - the userNeedsUpdate() function doesn't account well for keys being defined in the the old data but not in the new
  useEffect(() => {
    if (
      client &&
      client.getContext().user &&
      roverStatsigUser &&
      // check that roverStatsigUser is not all undefined values
      Object.values(roverStatsigUser).some((value) => value !== undefined) &&
      userNeedsUpdate(client.getContext().user, roverStatsigUser)
    ) {
      client
        .updateUserAsync(createUserPayload(roverStatsigUser, client.getContext().user))
        .catch((error) => {
          Logger.error(error);
        });
    }
  }, [client, roverStatsigUser]);

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
};

export default RoverStatsigProvider;
